// ─────────────────────────────────────────────────────────────────────────────
// RefundStatus.jsx  — single file, all sub-components inlined
// ─────────────────────────────────────────────────────────────────────────────
import React, { useRef, useState, useMemo, useCallback } from "react";
import {
  Box, Card, CardContent, Stack, Typography, IconButton,
  Button, Divider, Chip, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import ShareIcon        from "@mui/icons-material/Share";
import ExpandMoreIcon   from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import html2canvas      from "html2canvas";
import { toast }        from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

// ── Assets ────────────────────────────────────────────────────────────────────
import step1                  from "../../../assets/step1.png";
import step2                  from "../../../assets/step2.png";
import YouWon                 from "../../../assets/YouWon.gif";
import logoReceipt            from "../../../assets/logo-receipt.png";
import RefundedSuccess        from "../../../assets/RefundedSucces.png";
import RefundeInitfailed      from "../../../assets/refundedfailed.png";
import OrderSuccessImg        from "../../../assets/Order_Success.png";
import REFUND_UPON_REQUEST_IMG from "../../../assets/REFUND_UPON_REQUEST_IMG.png";
import SearchGif              from "../../../assets/Searching.gif";
import Cliam                  from "../../../assets/helpClaimBg.gif";
import refundfailed           from "../../../assets/refundfailed.png";

// ── External sub-components (exist elsewhere in your project) ─────────────────
import OrderHeader                   from "./OrderHeader";
import RefundStatusCard              from "./RefundStatusCard";
import TicketRaisedModal             from "./TicketRaisedModal";
import WhatsAppNumberModal           from "./WhatsAppModal";
import RefundProcessingReceipt       from "../RefundProcessingReceipt";
import RefundSuccessfulReceipt       from "../RefundSuccessfulReceipt";
import RefundUponRequestReceipt      from "../RefundUponRequestReceipt";
import OrderSuccessReceipt           from "../OrderSuccessReceipt";
import RefundInitiationFailedReceipt from "../RefundInitiationFailedReceipt";
import RefundFailedReceipt           from "../RefundFailedReceipt";
import RefundScheduledReceipt        from "../RefundScheduledReceipt";


import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ReceiptOutlinedIcon       from "@mui/icons-material/ReceiptOutlined";
import CreditCardOutlinedIcon    from "@mui/icons-material/CreditCardOutlined";


 

 

 
const MetaRow = ({ icon: Icon, label, value, bold = false }) => (
  <Box sx={ROW_STYLE}>

    <span>
      {label && <>{label}: </>}
      {bold
        ? <strong style={{ color: "#1F2937", fontWeight: 500 }}>{value}</strong>
        : value}
    </span>
  </Box>
);
// ═════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═════════════════════════════════════════════════════════════════════════════

/** UTC date-string → readable IST string. Returns "N/A" for falsy input. */
const toIST = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr + "Z").toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric", month: "long", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};

/** Statuses that show the Reward Unlocked banner. */
const REWARD_STATUSES = new Set([
  "REFUND_SCHEDULED",
  "REFUND_INITIATED",
  "REFUND_SUCCESSFUL",
]);

/** Chip config — add a new status chip here without touching JSX. */
const CHIP_CONFIG = [
  { key: "successQuantity", label: "Success", bgcolor: "#e6f6f0", color: "#00a676" },
  { key: "failureQuantity", label: "Failed",  bgcolor: "#ffeaea", color: "#d32f2f" },
  { key: "vendRemaining",   label: "Pending", bgcolor: "#fff4e5", color: "#F28618" },
];

const TABLE_BG  = "rgba(238,247,244,0.88)";
const ROW_STYLE = { fontSize: 12, color: "#4B5563", marginBottom: 1, fontFamily:"roboto" };


// ═════════════════════════════════════════════════════════════════════════════
// PURE / PRESENTATIONAL SUB-COMPONENTS  (no external deps beyond MUI + assets)
// ═════════════════════════════════════════════════════════════════════════════

// ── Status chips for one order-line-item row ──────────────────────────────────
const StatusChips = ({ item }) => (
  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
    {CHIP_CONFIG.map(({ key, label, bgcolor, color }) =>
      item[key] > 0 ? (
        <Chip
          key={key}
          label={`${label} (${item[key]})`}
          style={{ fontSize: 12 }}
          sx={{ fontWeight: 600, bgcolor, color }}
        />
      ) : null
    )}
  </Box>
);

// ── Reusable accordion toggle header ─────────────────────────────────────────
const SectionToggle = ({ label, open, onToggle }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => e.key === "Enter" && onToggle()}
    sx={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      cursor: "pointer", bgcolor: TABLE_BG, p: 2, borderRadius: 2, outline: "none",
      "&:hover": { bgcolor: "rgba(238,247,244,0.95)" },
    }}
  >
    <Typography fontWeight={700} fontSize={14}>{label}</Typography>
    <IconButton
      size="small"
      tabIndex={-1}
      sx={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s", pointerEvents: "none" }}
    >
      <ExpandMoreIcon />
    </IconButton>
  </Box>
);

// ── Order ID + date header row ────────────────────────────────────────────────
const OrderCardHeader = ({ orderId, paymentTime, showShare, onShare }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
    <Box>
      <Typography fontWeight={700} fontSize={15}><span style={{    visibility: "hidden"}}>-</span>Order_{orderId}</Typography>
      <Typography variant="body2" color="text.secondary" fontSize={12}>
       <span style={{    visibility: "hidden"}}>-</span> {toIST(paymentTime)}
      </Typography>
    </Box>
    {showShare && (
      <IconButton onClick={onShare} aria-label="Share receipt" size="small">
        <ShareIcon fontSize="small" />
      </IconButton>
    )}
  </Stack>
);

// ── Collapsible order items table (owns its own open state) ───────────────────
const OrderItemsTable = ({ orderLineItem = [] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box mt={2}>
      <SectionToggle
        label={`Order : ${orderLineItem.length} Items`}
        open={open}
        onToggle={() => setOpen((p) => !p)}
      />
      <Collapse in={open}>
        <TableContainer
          component={Paper}
          sx={{ mt: 1, borderRadius: 3, boxShadow: "none", border: "1px solid #eee", bgcolor: TABLE_BG }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { label: "Item (Count)",   align: "left"   },
                  { label: "Price Per Unit", align: "center" },
                  { label: "Status",         align: "right"  },
                ].map(({ label, align }) => (
                  <TableCell
                    key={label}
                    align={align}
                    style={{ backgroundColor: TABLE_BG, fontSize: 12, fontWeight: 500 }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orderLineItem.map((item, idx) => (
                <TableRow key={item.id ?? idx}>
                  <TableCell style={{ fontSize: 12 }}>{item.variantName} ({item.itemQuantity})</TableCell>
                  <TableCell style={{ fontSize: 12 }} align="center">₹{item.amount}</TableCell>
                  <TableCell style={{ fontSize: 12 }} align="right">
                    <StatusChips item={item} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
};

// ── Collapsible payment details (owns its own open state) ─────────────────────
const PaymentDetails = ({ paidAmount, paidDate, transactionId, payMethod }) => {
  const [open, setOpen] = useState(false);
 
  return (
    <Box mt={2} sx={{ bgcolor: TABLE_BG, borderRadius: 2 }}>
      <SectionToggle
        label={`Paid Amount : ₹${paidAmount}`}
        open={open}
        onToggle={() => setOpen((p) => !p)}
      />
 
      <Collapse in={open}>
        <Box p={2} sx={{ borderTop: "1px solid #d3ece3" }}>
          <MetaRow
            icon={CalendarTodayOutlinedIcon}
            value={toIST(paidDate)}
          />
          <MetaRow
            icon={ReceiptOutlinedIcon}
            label="Transaction ID"
            value={transactionId}
            bold
          />
          <MetaRow
            icon={CreditCardOutlinedIcon}
            label="Payment Method"
            value={payMethod}
            bold
          />
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Reward Unlocked banner ────────────────────────────────────────────────────
const RewardBanner = ({ onClaim }) => (
  <Box
    mt={1}
    display="flex"
    justifyContent="center"
    sx={{
      position: "relative", borderRadius: 3, overflow: "hidden",
      p: { xs: 3, md: 4 }, paddingLeft: 0, paddingBottom: 0,
      background: "linear-gradient(to bottom, #A1C6B8 0%, #EEF6F3 100%)",
      backgroundImage: `url(${Cliam})`, backgroundSize: "cover",
    }}
  >
    <img src={YouWon} alt="Reward animation" style={{ width: 100, position: "relative", zIndex: 9999 }} />

    {/* Tinted overlay */}
    <Box aria-hidden sx={{
      position: "absolute", inset: 0,
      bgcolor: "rgba(238,247,244,0.88)",
      backgroundImage: `url(${Cliam})`, backgroundSize: "cover",
    }} />

    <Box sx={{ position: "relative" }}>
      <Typography fontWeight={800} fontSize={16} textAlign="start">Reward Unlocked!</Typography>
      <Typography variant="body2" fontSize={12} mb={0}>You've Won Cashback On This Order</Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Button
          variant="contained"
          onClick={onClaim}
          style={{ position: "absolute", zIndex: 999 }}
          endIcon={
            <Box sx={{
              bgcolor: "#fff", color: "#00a676", borderRadius: "50%",
              width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ArrowForwardIcon fontSize="small" />
            </Box>
          }
          sx={{
            bgcolor: "#00a676", borderRadius: 8, fontWeight: 700,
            pl: "8px", pr: "8px", height: "28px", mt: "5px",
            "& .MuiButton-endIcon": { marginLeft: "16px" },
            "&:hover": { bgcolor: "#008f63" },
          }}
        >
          Claim Now
        </Button>
      </Box>
      <img src={Cliam} alt="" aria-hidden style={{ position: "absolute", top: "-14px", right: "-24px" }} />
    </Box>
  </Box>
);

// ── Help / raise ticket strip ─────────────────────────────────────────────────
const HelpSection = ({ onRaiseTicket, refundStatus, bottomSpacing = false }) => {
  const isFailed = refundStatus === "REFUND_INITIATION_FAILED";
  return (
    <Box
      mt={2}
      mb={bottomSpacing ? "50px" : 0}
      sx={{
        bgcolor: "#eef7f4", borderRadius: 3,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 2, padding: "12px",
      }}
    >
      <img src={SearchGif} alt="" aria-hidden style={{ width: 90 }} />
      <Box textAlign="center">
        <Typography fontWeight={700} mb={1} fontSize={14}>Have Any Concerns?</Typography>
        <Button
          onClick={onRaiseTicket}
          variant="outlined"
          sx={{
            borderRadius: 2, textTransform: "none", fontWeight: 600,
            color:           isFailed ? "#ffffff" : "#00a676",
            backgroundColor: isFailed ? "#00a676" : "#ffffff",
            borderColor:     "#00a676",
            "&:hover": {
              borderColor:     "#008f63",
              backgroundColor: isFailed ? "#008f63" : "#f0fdf4",
              color:           isFailed ? "#ffffff" : "#008f63",
            },
          }}
        >
          Raise A Ticket
        </Button>
      </Box>
    </Box>
  );
};

// ── Invisible html2canvas capture target ──────────────────────────────────────
const HiddenReceipt = ({
  visible, receiptRef, orderReport,
  formatpaymentTime, formatrefundInitiatedTime,
  formatrefundCreditExpectedTime, formatrefundCreditTime,
  totalRefundAmount,
}) => {
  const refund  = orderReport?.data?.refundDetailInfoList?.[0];
  const status  = refund?.status;
  const orderId = orderReport?.data?.orderId;

  // status → receipt component (no if/else chain)
  const RECEIPT_MAP = {
    REFUND_INITIATED: (
      <RefundProcessingReceipt orderReport={orderReport}
        formatrefundInitiatedTime={formatrefundInitiatedTime}
        formatrefundCreditExpectedTime={formatrefundCreditExpectedTime}
        formatpaymentTime={formatpaymentTime} step1Img={step1} />
    ),
    REFUND_SUCCESSFUL: (
      <RefundSuccessfulReceipt orderReport={orderReport}
        formatrefundInitiatedTime={formatrefundInitiatedTime}
        formatrefundCreditTime={formatrefundCreditTime}
        formatpaymentTime={formatpaymentTime} step2Img={step2} />
    ),
    REFUND_UPON_REQUEST: (
      <RefundUponRequestReceipt orderReport={orderReport}
        formatpaymentTime={formatpaymentTime} refundImage={REFUND_UPON_REQUEST_IMG} />
    ),
    REFUND_NOT_REQUIRED: (
      <OrderSuccessReceipt orderReport={orderReport}
        formatpaymentTime={formatpaymentTime} OrderSuccessImg={OrderSuccessImg} />
    ),
    REFUND_INITIATION_FAILED: (
      <RefundInitiationFailedReceipt orderReport={orderReport}
        formatpaymentTime={formatpaymentTime} failedImg={refundfailed} />
    ),
    REFUND_FAILED: (
      <RefundFailedReceipt orderReport={orderReport}
        formatpaymentTime={formatpaymentTime} failedImg={RefundeInitfailed} />
    ),
    REFUND_SCHEDULED: (
      <RefundScheduledReceipt orderReport={orderReport}
        formatrefundCreditExpectedTime={formatrefundCreditExpectedTime}
        formatpaymentTime={formatpaymentTime}
        RefundedSuccess={RefundedSuccess} totalRefundAmount={totalRefundAmount} />
    ),
  };

  return (
    <div aria-hidden style={{ position: "absolute", zIndex: -100, display: visible ? undefined : "none", width: "100%" }}>
      <div ref={receiptRef}
        className="max-w-md mx-auto mt-10 rounded-2xl shadow-md p-4 font-sans text-sm"
        style={{ backgroundColor: "rgb(253 224 71)" }}
      >
        <div className="bg-white rounded-xl p-4" style={{ marginBottom: "20vw", marginTop: "20vw" }}>
          <h2 className="text-gray-800 font-semibold text-base">Order_{orderId}</h2>
          <p className="text-gray-500 text-xs mb-4">
            {refund?.paymentTime ? formatpaymentTime : "Not Available"}
          </p>
          {RECEIPT_MAP[status] ?? null}
          <div className="mt-5 flex justify-center">
            <img src={logoReceipt} alt="Daalchini Logo" className="h-10" />
          </div>
        </div>
      </div>
    </div>
  );
};


// ═════════════════════════════════════════════════════════════════════════════
// CUSTOM HOOK — html2canvas + Web Share API
// ═════════════════════════════════════════════════════════════════════════════
const useShareReceipt = (setLoader) => {
  const receiptRef = useRef(null);
  const [shareVisible, setShareVisible] = useState(false);

  const handleShare = useCallback(async () => {
    if (!receiptRef.current) {
      toast.error("Receipt reference is missing!", { position: "top-right", autoClose: 5000 });
      return;
    }

    setShareVisible(true);
    setLoader?.(true);

    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2, scrollY: 0, useCORS: true,
        windowWidth:  receiptRef.current.scrollWidth,
        windowHeight: receiptRef.current.scrollHeight,
      });

      const imageDataUrl = canvas.toDataURL("image/png");
      const blob = await (await fetch(imageDataUrl)).blob();
      const file = new File([blob], "receipt.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ title: "Order Receipt", text: "Here is your order receipt.", files: [file] });
        toast.success("Receipt shared successfully!", { position: "top-right", autoClose: 3000 });
      } else {
        const link = document.createElement("a");
        link.href = imageDataUrl;
        link.download = "receipt.png";
        link.click();
        toast.info("Download started (share not supported on this device)", { position: "top-right", autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.statusMessage ?? "Failed to share receipt!", { position: "top-right", autoClose: 5000 });
    } finally {
      setShareVisible(false);
      setLoader?.(false);
    }
  }, [setLoader]);

  return { receiptRef, shareVisible, handleShare };
};


// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═════════════════════════════════════════════════════════════════════════════
export default function RefundStatus(props) {
  const location = useLocation();
  const navigate = useNavigate();

  // ── URL params ──────────────────────────────────────────────────────────────
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isWebview    = searchParams.get("webview");
  const mobileNumber = searchParams.get("mobileNumber");
  const id           = searchParams.get("id");

  // ── Safely derive all data from props ───────────────────────────────────────
  const orderReport  = props?.orderReport;
  const refundList   = orderReport?.data?.refundDetailInfoList ?? [];
  const firstRefund  = refundList[0] ?? {};

  const refundStatus  = firstRefund.status;
  const orderId       = orderReport?.data?.orderId       ?? "Order not found";
  const orderLineItem = orderReport?.data?.orderLineItem ?? [];
  const paidAmount    = firstRefund.paidAmount    ?? "N/A";
  const refundAmount  = firstRefund.refundAmount  ?? "N/A";
  const transactionId = firstRefund.transactionId ?? "N/A";
  const payMethod     = firstRefund.payMethod     ?? "Unknown Payment Method";

  // ── Memoised IST-formatted dates ────────────────────────────────────────────
  const formatpaymentTime              = useMemo(() => toIST(firstRefund.paymentTime),              [firstRefund]);
  const formatrefundInitiatedTime      = useMemo(() => toIST(firstRefund.refundInitiatedTime),      [firstRefund]);
  const formatrefundCreditExpectedTime = useMemo(() => toIST(firstRefund.refundCreditExpectedTime), [firstRefund]);
  const formatrefundCreditTime         = useMemo(() => toIST(firstRefund.refundCreditTime),         [firstRefund]);
  const totalRefundAmount              = useMemo(
    () => refundList.reduce((sum, r) => sum + (r.refundAmount ?? 0), 0),
    [refundList]
  );

  // ── Share hook ──────────────────────────────────────────────────────────────
  const { receiptRef, shareVisible, handleShare } = useShareReceipt(props?.setLoader);

  // ── Claim Now navigation ────────────────────────────────────────────────────
  const handleClaim = useCallback(() => {
    if (isWebview) {
      window.location.href = `https://www.daalchini.co.in/tagOrderId?value=${orderId}`;
    } else {
      navigate(`/tagOrderId?value=${orderId}`);
    }
  }, [isWebview, orderId, navigate]);

  // ── Raise ticket (URL param → localStorage → modal) ────────────────────────
  const handleRaiseTicket = useCallback(() => {
    const number = mobileNumber || localStorage.getItem("mobileNo");
    if (number) {
      props?.setWhatsappnum(number);
      props?.handleTicket(number);
    } else {
      props?.setOpenWhatsappModal(true);
    }
  }, [mobileNumber, props]);

  const addBottomSpacing = isWebview === "true" && !id;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {!isWebview && <OrderHeader />}

      {props?.tickeRaiseSuccesFlag && (
        <TicketRaisedModal
          setTickeRaiseSuccesFlag={props.setTickeRaiseSuccesFlag}
          raiseTicketData={props.raiseTicketData}
          orderReport={orderReport}
        />
      )}

      {props?.openWhatsappModal && (
        <WhatsAppNumberModal
          handleTicket={props.handleTicket}
          setWhatsappnum={props.setWhatsappnum}
          whatsappnum={props.whatsappnum}
          setOpenWhatsappModal={props.setOpenWhatsappModal}
        />
      )}

      {/* Invisible capture target for screenshot sharing */}
      <HiddenReceipt
        visible={shareVisible}
        receiptRef={receiptRef}
        orderReport={orderReport}
        formatpaymentTime={formatpaymentTime}
        formatrefundInitiatedTime={formatrefundInitiatedTime}
        formatrefundCreditExpectedTime={formatrefundCreditExpectedTime}
        formatrefundCreditTime={formatrefundCreditTime}
        totalRefundAmount={totalRefundAmount}
      />

      <Box sx={{ bgcolor: "#ffffff", minHeight: "100vh", display: "flex", justifyContent: "center", p: 2 }}>
        <Card sx={{ width: "100%", maxWidth: 900, borderRadius: 4, boxShadow: 0 }}>
          <CardContent style={{ boxShadow: "none", padding: 0 }}>

            <OrderCardHeader
              orderId={orderId}
              paymentTime={firstRefund.paymentTime}
              showShare={!isWebview}
              onShare={handleShare}
            />

            <RefundStatusCard
              refundStatus={refundStatus}
              refundDetailInfoList={refundList}
              payMethod={payMethod}
              refundAmount={refundAmount}
            />

            <OrderItemsTable orderLineItem={orderLineItem} />

            <PaymentDetails
              paidAmount={paidAmount}
              paidDate={firstRefund.paymentTime}
              transactionId={transactionId}
              payMethod={payMethod}
            />

            {REWARD_STATUSES.has(refundStatus) && (
              <RewardBanner onClaim={handleClaim} />
            )}

            <HelpSection
              onRaiseTicket={handleRaiseTicket}
              refundStatus={refundStatus}
              bottomSpacing={addBottomSpacing}
            />

          </CardContent>
        </Card>
      </Box>
    </>
  );
}